import { colors } from '@insurancemenu/imenu-components-lib';
import { ComponentStylesProps } from '@insurancemenu/imenu-components-lib/dist/types/global';
import { breakpoints, typography } from '@insurancemenu/imenu-components-lib/dist/utils';
import { createUseStyles } from 'react-jss';
import styled, { css, CSSProp } from 'styled-components';

export interface BrowsingContentThemeProps {
  comparedPortfoliosQuantity: number;
}

interface BrowsingCardWrapperStyledProps {
  $hasCrossSellPlan: boolean;
  $isAddedToCart: boolean;
  $hasSeveralProducts: boolean;
}

const styles: ComponentStylesProps<BrowsingContentThemeProps> = {
  layout: {
    backgroundColor: colors.neutral[100],
    display: 'flex',
  },
  browsingContentWrapper: {
    position: 'relative',
  },
  browsingSpinner: {
    position: 'fixed',
    top: '50%',
    transform: 'translateY(-50%)',
  },
  title: {
    fontSize: '3.4rem',
    lineHeight: '4rem',
    textAlign: 'center',
  },
  cardsList: {
    display: 'grid',
    gap: '3rem',
    maxWidth: '109rem',
    margin: '0 auto',
    gridTemplateColumns: 'repeat(auto-fit, minmax(52.8rem, 1fr))',
    '@media (max-width:1410px)': {
      maxWidth: '80%',
      justifyContent: 'center',
      gridTemplateColumns: 'repeat(auto-fit, minmax(20rem, 56rem))',
      transform: 'translateX(2.5rem)',
    },
    [breakpoints.lg.down]: {
      maxWidth: '100%',
      transform: 'none',
      justifyItems: 'stretch',
      justifyContent: 'center',
    },
  },
  browsingCard: {
    // Vertically stretch cards
    display: 'grid',
    gridTemplateRows: 'auto auto 1fr auto',
    height: 'auto',
  },
  browsingContent: {
    margin: '0 2rem 20rem',
  },
  browsingHeader: ({ comparedPortfoliosQuantity }) => ({
    maxWidth: '109rem',
    margin: {
      left: 'auto',
      right: 'auto',
      top: '5.6rem',
      bottom: comparedPortfoliosQuantity === 0 ? '6rem' : '2rem',
    },
  }),
  bcBlock: {
    marginBottom: '2.8rem',
    // Move to lib to prevent wide blocks with narrow titles
    width: 'max-content',
  },
  portfoliosQuantity: {
    fontSize: '1.6rem',
    maxWidth: '109rem',
    margin: '2rem auto',
    '@media (max-width:1410px)': {
      maxWidth: '50rem',
    },
  },
  compareBlock: {
    position: 'sticky',
    top: '0',
    zIndex: '6',
    right: '0',
    left: '25rem',
  },
  backButton: {
    position: 'absolute',
    left: '0',
    top: '50%',
    transform: 'translateY(-50%)',
  },
  openSidebarButton: {
    position: 'absolute',
    top: '-6.6rem', // to center with button in sidebar
    right: '-9.6rem',
    '@media (max-width:1440px)': {
      right: '0',
    },
  },
  headerTop: {
    position: 'relative',
  },
  hiddenButton: {
    visibility: 'hidden',
  },
  sidebarContentLayout: {
    maxWidth: '100%',
    width: '100%',
  },
  cancelButton: { marginRight: '1.6rem' },
  proposalSidebarContent: { width: '25rem' },
  bcForm: { paddingLeft: '2rem' },
  compareButton: {
    textTransform: 'uppercase',
    margin: '0 auto 2rem',
  },
  saveProposalButtonWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    '& span': {
      color: colors.red[600],
      fontSize: '1.4rem',
      textAlign: 'center',
      lineHeight: '2rem',
    },
    '& button': {
      minWidth: '21rem',
    },
  },
  withCrossSellPlan: {
    borderRadius: '0 0 10px 10px',
    /* The outline is removed because the cross-sell wrapper provides it. */
    outline: 'none',
  },
};

const createStyles = createUseStyles(styles);
export { createStyles };

export const BrowsingCardWrapperStyled = styled.div<BrowsingCardWrapperStyledProps>`
  display: grid;
  grid-template-columns: 1fr;

  /*
 * Cross-sell plan cards include both a label and card content,
 * while regular plan cards contain only the card content.
 *
 * Using 1fr allows each card to grow based on its content,
 * keeping cards with different layouts aligned.
 */

  grid-template-rows: ${({ $hasCrossSellPlan }): string => ($hasCrossSellPlan ? 'auto 1fr' : '1fr')};

  /* Values are reused from the browsing card styles. */
  max-width: ${({ $hasSeveralProducts }): string => ($hasSeveralProducts ? '110.8rem' : '52.8rem')};

  ${({ $hasCrossSellPlan, $isAddedToCart }): CSSProp =>
    $hasCrossSellPlan && $isAddedToCart
      ? css`
          border-radius: 10px;
          outline: 0.4rem solid ${colors.green[600]};
        `
      : css`
          outline: none;
        `}
`;

export const CrossSellPlanLabelStyled = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  ${typography.bold[14]}
  color: ${colors.purple[600]};
  background-color: ${colors.purple[100]};
  padding: 1rem;
  border-bottom: 1px solid ${colors.purple[600]};
  border-radius: 10px 10px 0 0;
`;
